import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import {
  TREND_GRANULARITIES,
  type AdProvider,
  type AdvertisingOverview,
  type AdReconciliationQuery,
  type TrendGranularity,
} from '@/api/advertisingOverview'
import { ADVERTISING_LEVELS, type AdvertisingLevel, type AttributionModel } from '@/api/types'
import { DEFAULT_MODEL } from '@/features/analytics/advertising/advertisingQuery'
import { addDays, resolvePeriod, type GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'
import { useUrlChoice } from '@/lib/useUrlChoice'

/** The part of the overview query a section reads: every section of it shares one request. */
export interface OverviewQueryLike {
  isPending: boolean
  isError: boolean
  error: unknown
  data: AdvertisingOverview | undefined
}

/** How many campaigns "Top campaigns by ROAS" charts — the API's default `limit`. */
export const TOP_CAMPAIGNS = 5

const GRANULARITY_PARAM = 'adv_granularity'
const LEVEL_PARAM = 'recon_level'

/** Day or week, in the URL beside the global filters, so a copied link reopens the same chart. */
export function useGranularity(): [TrendGranularity, (next: TrendGranularity) => void] {
  return useUrlChoice(GRANULARITY_PARAM, TREND_GRANULARITIES, 'day')
}

/** The reconciliation's level, in the URL. */
export function useReconciliationLevel(): [AdvertisingLevel, (next: AdvertisingLevel) => void] {
  return useUrlChoice(LEVEL_PARAM, ADVERTISING_LEVELS, 'campaign')
}

/** The root of the overview read's key: what a change to the tracking baseline marks stale (Settings › Tracking). */
export const advertisingOverviewKeys = { all: ['advertising-overview'] as const }

/**
 * Each section's own query, so one failing never blanks another. Keyed by the
 * canonical filter string (as the Overview's cards are), the provider and the model.
 */
export function useAdvertisingOverview(filters: GlobalFilters, provider: AdProvider, model: AttributionModel) {
  const client = useApiClient()
  return useQuery({
    queryKey: [...advertisingOverviewKeys.all, client.kind, filtersKey(filters), provider, model],
    queryFn: () => client.advertisingOverview({ filters, provider, model, limit: TOP_CAMPAIGNS }),
  })
}

export function useAdvertisingOverviewTrend(
  filters: GlobalFilters,
  provider: AdProvider,
  model: AttributionModel,
  granularity: TrendGranularity,
) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['advertising-overview-trend', client.kind, filtersKey(filters), provider, model, granularity],
    queryFn: () => client.advertisingOverviewTrend({ filters, provider, model, granularity }),
  })
}

/** How many weeks the all-sales card shows. */
export const ALL_SALES_WEEKS = 12

/**
 * The all-sales card's window: whole weeks ending on the global range's last day, so the
 * last bucket is the week the page is looking at. Every other filter still applies.
 */
export function lastWeeksFilters(filters: GlobalFilters, now: Date, weeks = ALL_SALES_WEEKS): GlobalFilters {
  const { current } = resolvePeriod(filters, now)
  return { ...filters, range: 'custom', from: addDays(current.to, 1 - 7 * weeks), to: current.to }
}

/**
 * Meta's spend against every sale, by week, over the last `ALL_SALES_WEEKS`. Its own
 * query and key: no attribution model changes the figures it shows, so it reads the
 * default model (the route requires one) and is not refetched when the model changes.
 */
export function useAllSalesTrend(filters: GlobalFilters, provider: AdProvider) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['advertising-overview-all-sales', client.kind, filtersKey(filters), provider],
    queryFn: () =>
      client.advertisingOverviewTrend({
        filters: lastWeeksFilters(filters, new Date()),
        provider,
        model: DEFAULT_MODEL,
        granularity: 'week',
      }),
  })
}

export function useAdReconciliation(query: AdReconciliationQuery) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['ad-attribution-reconciliation', client.kind, query],
    queryFn: () => client.adAttributionReconciliation(query),
  })
}
